import { useState } from 'react';
import { Link } from 'react-router-dom';
import { dashboardProfile } from '@/mocks/dashboard';
import { creatorProfiles } from '@/mocks/profiles';

// ─── IBAN helpers (same logic as Step3Goal) ───────────────────────────────────
const validateSaudiIBAN = (raw: string) => {
  const clean = raw.replace(/\s/g, '').toUpperCase();
  if (clean.length === 0) return { valid: false, status: 'empty' as const, message: '' };
  if (!clean.startsWith('SA')) return { valid: false, status: 'invalid' as const, message: 'الآيبان السعودي يجب أن يبدأ بـ SA' };
  if (clean.length < 24) return { valid: false, status: 'partial' as const, message: `${clean.length} / 24 حرف — أكمل رقم الآيبان` };
  if (clean.length > 24) return { valid: false, status: 'invalid' as const, message: 'رقم الآيبان يجب أن يكون 24 حرفاً بالضبط' };
  const rearranged = clean.slice(4) + clean.slice(0, 4);
  const numeric = rearranged.split('').map((c) => { const code = c.charCodeAt(0); return code >= 65 ? String(code - 55) : c; }).join('');
  let remainder = 0;
  for (let i = 0; i < numeric.length; i += 9) { remainder = Number(String(remainder) + numeric.slice(i, i + 9)) % 97; }
  if (remainder !== 1) return { valid: false, status: 'invalid' as const, message: 'رقم الآيبان غير صحيح — تحقق من الأرقام' };
  return { valid: true, status: 'valid' as const, message: 'آيبان سعودي صحيح ✓' };
};

const formatIBAN = (raw: string) => {
  const clean = raw.replace(/\s/g, '').toUpperCase().slice(0, 24);
  return clean.match(/.{1,4}/g)?.join(' ') ?? clean;
};

// ─── Types ────────────────────────────────────────────────────────────────────
type TabKey = 'profile' | 'bank' | 'notifications' | 'security';

const tabs: { key: TabKey; label: string; icon: string }[] = [
  { key: 'profile',       label: 'البيانات الشخصية', icon: 'ri-user-3-line' },
  { key: 'bank',          label: 'الحساب البنكي',    icon: 'ri-bank-line' },
  { key: 'notifications', label: 'الإشعارات',        icon: 'ri-notification-3-line' },
  { key: 'security',      label: 'الأمان',           icon: 'ri-shield-keyhole-line' },
];

// ─── Component ────────────────────────────────────────────────────────────────
const SettingsPage = () => {
  const profile = creatorProfiles[0];
  const [activeTab, setActiveTab] = useState<TabKey>('profile');
  const [saved, setSaved] = useState<string | null>(null);

  // Profile state
  const [displayName, setDisplayName] = useState(dashboardProfile.displayName);
  const [bio, setBio] = useState(profile.bio);
  const [location, setLocation] = useState(profile.location);
  const [category, setCategory] = useState(profile.category);
  const [twitter, setTwitter] = useState(profile.socialLinks.twitter ?? '');
  const [instagram, setInstagram] = useState(profile.socialLinks.instagram ?? '');
  const [snapchat, setSnapchat] = useState(profile.socialLinks.snapchat ?? '');
  const [website, setWebsite] = useState(profile.socialLinks.website ?? '');

  // Bank state
  const [iban, setIban] = useState('SA03 8000 0000 6080 1016 7519');
  const [bankName, setBankName] = useState('بنك الراجحي');
  const ibanValidation = validateSaudiIBAN(iban);

  // Notification state
  const [notifSettings, setNotifSettings] = useState({
    donation:   true,
    milestone:  true,
    expiry:     true,
    review:     true,
    tip:        false,
    email:      true,
    whatsapp:   false,
  });

  // Security state
  const [currentPass, setCurrentPass] = useState('');
  const [newPass, setNewPass]         = useState('');
  const [confirmPass, setConfirmPass] = useState('');
  const [showPass, setShowPass]       = useState(false);
  const [twoFactor, setTwoFactor]     = useState(true);

  const showSaved = (section: string) => {
    setSaved(section);
    setTimeout(() => setSaved(null), 2500);
  };

  const ibanBorderClass = {
    empty:   'border-stone-200 focus:border-emerald-400',
    partial: 'border-amber-300 focus:border-amber-400',
    invalid: 'border-red-300 focus:border-red-400',
    valid:   'border-emerald-400 focus:border-emerald-500',
  }[ibanValidation.status];

  const ibanMsgClass = {
    empty:   '',
    partial: 'text-amber-600',
    invalid: 'text-red-500',
    valid:   'text-emerald-600',
  }[ibanValidation.status];

  return (
    <div className="min-h-screen bg-stone-50" dir="rtl">

      <div className="max-w-4xl mx-auto px-4 md:px-6 pt-24 pb-16">

        {/* Header */}
        <div className="flex items-center gap-3 mb-8">
          <Link
            to="/dashboard"
            className="w-9 h-9 flex items-center justify-center rounded-xl bg-white border border-stone-200 text-stone-500 hover:bg-stone-50 cursor-pointer transition-colors"
          >
            <i className="ri-arrow-right-line text-base"></i>
          </Link>
          <div>
            <h1 className="text-xl font-black text-stone-800">إعدادات الحساب</h1>
            <p className="text-xs text-stone-400">إدارة بياناتك الشخصية وتفضيلاتك</p>
          </div>
        </div>

        <div className="flex flex-col lg:flex-row gap-6">

          {/* Sidebar Tabs */}
          <aside className="w-full lg:w-56 shrink-0">
            <div className="bg-white rounded-2xl border border-stone-100 p-2 flex lg:flex-col gap-1 overflow-x-auto lg:overflow-visible">
              {tabs.map((tab) => (
                <button
                  key={tab.key}
                  onClick={() => setActiveTab(tab.key)}
                  className={`flex items-center gap-2.5 px-3 py-2.5 rounded-xl text-sm font-semibold transition-all cursor-pointer whitespace-nowrap w-full text-right ${
                    activeTab === tab.key
                      ? 'bg-emerald-600 text-white'
                      : 'text-stone-600 hover:bg-stone-50'
                  }`}
                >
                  <div className="w-5 h-5 flex items-center justify-center shrink-0">
                    <i className={`${tab.icon} text-base`}></i>
                  </div>
                  {tab.label}
                </button>
              ))}
            </div>
          </aside>

          {/* Content */}
          <div className="flex-1 min-w-0">

            {/* ── Profile Tab ── */}
            {activeTab === 'profile' && (
              <div className="space-y-5">
                {/* Avatar */}
                <div className="bg-white rounded-2xl border border-stone-100 p-5">
                  <h2 className="font-bold text-stone-800 mb-4 text-sm">الصورة الشخصية</h2>
                  <div className="flex items-center gap-4">
                    <div className="w-16 h-16 shrink-0">
                      <img
                        src={dashboardProfile.avatar}
                        alt={dashboardProfile.displayName}
                        className="w-16 h-16 rounded-2xl object-cover object-top border-2 border-stone-100"
                      />
                    </div>
                    <div>
                      <button className="flex items-center gap-2 px-4 py-2 rounded-xl bg-stone-100 hover:bg-stone-200 text-stone-700 text-sm font-semibold cursor-pointer transition-colors whitespace-nowrap">
                        <i className="ri-upload-2-line text-sm"></i>
                        تغيير الصورة
                      </button>
                      <p className="text-xs text-stone-400 mt-1.5">JPG أو PNG، حجم أقصى 2MB</p>
                    </div>
                  </div>
                </div>

                {/* Basic Info */}
                <div className="bg-white rounded-2xl border border-stone-100 p-5">
                  <h2 className="font-bold text-stone-800 mb-4 text-sm">المعلومات الأساسية</h2>
                  <div className="space-y-4">
                    <div>
                      <label className="block text-xs font-semibold text-stone-600 mb-1.5">الاسم الظاهر</label>
                      <input
                        type="text"
                        value={displayName}
                        onChange={(e) => setDisplayName(e.target.value)}
                        className="w-full border border-stone-200 rounded-xl px-4 py-2.5 text-sm text-stone-800 focus:outline-none focus:border-emerald-400 transition-colors"
                      />
                    </div>
                    <div>
                      <label className="block text-xs font-semibold text-stone-600 mb-1.5">نبذة شخصية</label>
                      <textarea
                        value={bio}
                        onChange={(e) => setBio(e.target.value)}
                        rows={3}
                        maxLength={300}
                        className="w-full border border-stone-200 rounded-xl px-4 py-2.5 text-sm text-stone-800 focus:outline-none focus:border-emerald-400 transition-colors resize-none"
                      />
                      <p className="text-xs text-stone-400 text-left mt-1">{bio.length} / 300</p>
                    </div>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                      <div>
                        <label className="block text-xs font-semibold text-stone-600 mb-1.5">المدينة</label>
                        <input
                          type="text"
                          value={location}
                          onChange={(e) => setLocation(e.target.value)}
                          className="w-full border border-stone-200 rounded-xl px-4 py-2.5 text-sm text-stone-800 focus:outline-none focus:border-emerald-400 transition-colors"
                        />
                      </div>
                      <div>
                        <label className="block text-xs font-semibold text-stone-600 mb-1.5">التخصص</label>
                        <select
                          value={category}
                          onChange={(e) => setCategory(e.target.value)}
                          className="w-full border border-stone-200 rounded-xl px-4 py-2.5 text-sm text-stone-800 focus:outline-none focus:border-emerald-400 transition-colors bg-white cursor-pointer"
                        >
                          {['تقنية وتعليم', 'إبداع وفنون', 'ريادة أعمال', 'محتوى رقمي', 'بيئة وزراعة', 'طعام ومطاعم'].map((c) => (
                            <option key={c} value={c}>{c}</option>
                          ))}
                        </select>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Social Links */}
                <div className="bg-white rounded-2xl border border-stone-100 p-5">
                  <h2 className="font-bold text-stone-800 mb-4 text-sm">روابط التواصل الاجتماعي</h2>
                  <div className="space-y-3">
                    {[
                      { icon: 'ri-twitter-x-line', label: 'تويتر / X', value: twitter, setter: setTwitter, prefix: '@' },
                      { icon: 'ri-instagram-line', label: 'إنستغرام', value: instagram, setter: setInstagram, prefix: '@' },
                      { icon: 'ri-snapchat-line', label: 'سناب شات', value: snapchat, setter: setSnapchat, prefix: '@' },
                      { icon: 'ri-global-line', label: 'الموقع الإلكتروني', value: website, setter: setWebsite, prefix: 'https://' },
                    ].map((social) => (
                      <div key={social.label} className="flex items-center gap-3">
                        <div className="w-9 h-9 flex items-center justify-center rounded-xl bg-stone-100 shrink-0">
                          <i className={`${social.icon} text-stone-500 text-base`}></i>
                        </div>
                        <div className="flex-1 flex items-center border border-stone-200 rounded-xl overflow-hidden focus-within:border-emerald-400 transition-colors">
                          <span className="px-3 py-2.5 text-xs text-stone-400 bg-stone-50 border-l border-stone-200 whitespace-nowrap">{social.prefix}</span>
                          <input
                            type="text"
                            value={social.value}
                            onChange={(e) => social.setter(e.target.value)}
                            placeholder={social.label}
                            className="flex-1 px-3 py-2.5 text-sm text-stone-800 focus:outline-none bg-white"
                          />
                        </div>
                      </div>
                    ))}
                  </div>
                </div>

                <button
                  onClick={() => showSaved('profile')}
                  className="w-full flex items-center justify-center gap-2 py-3 rounded-xl bg-emerald-600 hover:bg-emerald-700 text-white font-bold text-sm cursor-pointer transition-colors whitespace-nowrap"
                >
                  {saved === 'profile' ? (
                    <><i className="ri-check-line"></i> تم الحفظ!</>
                  ) : (
                    <><i className="ri-save-line"></i> حفظ التغييرات</>
                  )}
                </button>
              </div>
            )}

            {/* ── Bank Tab ── */}
            {activeTab === 'bank' && (
              <div className="space-y-5">
                <div className="bg-white rounded-2xl border border-stone-100 p-5">
                  <div className="flex items-center gap-3 mb-5">
                    <div className="w-10 h-10 flex items-center justify-center bg-emerald-50 rounded-xl shrink-0">
                      <i className="ri-bank-line text-emerald-600 text-lg"></i>
                    </div>
                    <div>
                      <h2 className="font-bold text-stone-800 text-sm">معلومات الحساب البنكي</h2>
                      <p className="text-xs text-stone-400">يُستخدم لتحويل مبالغ حملاتك</p>
                    </div>
                  </div>

                  <div className="space-y-4">
                    <div>
                      <label className="block text-xs font-semibold text-stone-600 mb-1.5">البنك</label>
                      <select
                        value={bankName}
                        onChange={(e) => setBankName(e.target.value)}
                        className="w-full border border-stone-200 rounded-xl px-4 py-2.5 text-sm text-stone-800 focus:outline-none focus:border-emerald-400 transition-colors bg-white cursor-pointer"
                      >
                        {['بنك الراجحي', 'البنك الأهلي السعودي', 'بنك الرياض', 'البنك السعودي الفرنسي', 'بنك البلاد', 'بنك الجزيرة', 'بنك ساب', 'بنك الإنماء'].map((b) => (
                          <option key={b} value={b}>{b}</option>
                        ))}
                      </select>
                    </div>

                    <div>
                      <label className="block text-xs font-semibold text-stone-600 mb-1.5">
                        رقم الآيبان (IBAN) <span className="text-red-400">*</span>
                      </label>
                      <p className="text-xs text-stone-400 mb-2">
                        مثال: <span className="font-mono tracking-wider">SA03 8000 0000 6080 1016 7519</span>
                      </p>
                      <div className="relative">
                        <input
                          type="text"
                          value={iban}
                          onChange={(e) => {
                            const raw = e.target.value.replace(/\s/g, '').toUpperCase().replace(/[^A-Z0-9]/g, '');
                            setIban(formatIBAN(raw));
                          }}
                          placeholder="SA00 0000 0000 0000 0000 0000"
                          maxLength={29}
                          dir="ltr"
                          className={`w-full border-2 rounded-xl px-4 py-3 text-sm font-mono tracking-wider text-stone-800 focus:outline-none transition-colors ${ibanBorderClass}`}
                        />
                        {ibanValidation.status !== 'empty' && (
                          <div className="absolute left-3 top-1/2 -translate-y-1/2">
                            {ibanValidation.status === 'valid' && <i className="ri-checkbox-circle-line text-emerald-500 text-sm"></i>}
                            {ibanValidation.status === 'invalid' && <i className="ri-close-circle-line text-red-500 text-sm"></i>}
                            {ibanValidation.status === 'partial' && <i className="ri-time-line text-amber-500 text-sm"></i>}
                          </div>
                        )}
                      </div>
                      {ibanValidation.status !== 'empty' && (
                        <p className={`text-xs mt-1.5 font-medium ${ibanMsgClass}`}>{ibanValidation.message}</p>
                      )}
                    </div>
                  </div>
                </div>

                {/* Fee reminder */}
                <div className="bg-amber-50 border border-amber-200 rounded-2xl p-4 flex items-start gap-3">
                  <div className="w-8 h-8 flex items-center justify-center shrink-0">
                    <i className="ri-percent-line text-amber-600 text-lg"></i>
                  </div>
                  <div>
                    <p className="text-sm font-bold text-amber-800 mb-0.5">تذكير بالرسوم الإدارية</p>
                    <p className="text-xs text-amber-700 leading-relaxed">
                      تُخصم رسوم إدارية بنسبة <strong>5%</strong> من إجمالي التبرعات تلقائياً عند التحويل لدعم استمرار المنصة غير الربحية.
                    </p>
                  </div>
                </div>

                <button
                  onClick={() => showSaved('bank')}
                  disabled={!ibanValidation.valid}
                  className={`w-full flex items-center justify-center gap-2 py-3 rounded-xl font-bold text-sm cursor-pointer transition-colors whitespace-nowrap ${
                    ibanValidation.valid
                      ? 'bg-emerald-600 hover:bg-emerald-700 text-white'
                      : 'bg-stone-100 text-stone-400 cursor-not-allowed'
                  }`}
                >
                  {saved === 'bank' ? (
                    <><i className="ri-check-line"></i> تم الحفظ!</>
                  ) : (
                    <><i className="ri-save-line"></i> حفظ معلومات البنك</>
                  )}
                </button>
              </div>
            )}

            {/* ── Notifications Tab ── */}
            {activeTab === 'notifications' && (
              <div className="space-y-5">
                {/* In-app */}
                <div className="bg-white rounded-2xl border border-stone-100 p-5">
                  <h2 className="font-bold text-stone-800 mb-1 text-sm">إشعارات التطبيق</h2>
                  <p className="text-xs text-stone-400 mb-4">اختر ما تريد أن تُنبَّه به داخل المنصة</p>
                  <div className="space-y-3">
                    {[
                      { key: 'donation',  icon: 'ri-heart-line',         color: 'text-emerald-500', label: 'تبرع جديد',           desc: 'عند استلام أي تبرع لحملاتك' },
                      { key: 'milestone', icon: 'ri-trophy-line',        color: 'text-amber-500',   label: 'إنجاز هدف',           desc: 'عند وصول حملتك لنسبة معينة' },
                      { key: 'expiry',    icon: 'ri-timer-flash-line',   color: 'text-red-500',     label: 'اقتراب انتهاء الحملة', desc: 'قبل 5 أيام من انتهاء الحملة' },
                      { key: 'review',    icon: 'ri-shield-check-line',  color: 'text-sky-500',     label: 'نتيجة المراجعة',      desc: 'عند الموافقة أو رفض حملتك' },
                      { key: 'tip',       icon: 'ri-lightbulb-line',     color: 'text-orange-400',  label: 'نصائح وتوصيات',       desc: 'نصائح لتحسين أداء حملاتك' },
                    ].map((item) => (
                      <div key={item.key} className="flex items-center justify-between gap-4 py-2 border-b border-stone-50 last:border-0">
                        <div className="flex items-center gap-3">
                          <div className="w-8 h-8 flex items-center justify-center rounded-xl bg-stone-50 shrink-0">
                            <i className={`${item.icon} ${item.color} text-sm`}></i>
                          </div>
                          <div>
                            <p className="text-sm font-semibold text-stone-700">{item.label}</p>
                            <p className="text-xs text-stone-400">{item.desc}</p>
                          </div>
                        </div>
                        <button
                          onClick={() => setNotifSettings((prev) => ({ ...prev, [item.key]: !prev[item.key as keyof typeof prev] }))}
                          className={`w-11 h-6 rounded-full transition-colors relative shrink-0 cursor-pointer ${notifSettings[item.key as keyof typeof notifSettings] ? 'bg-emerald-500' : 'bg-stone-200'}`}
                        >
                          <div className={`absolute top-0.5 w-5 h-5 bg-white rounded-full transition-all ${notifSettings[item.key as keyof typeof notifSettings] ? 'left-5' : 'left-0.5'}`}></div>
                        </button>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Channels */}
                <div className="bg-white rounded-2xl border border-stone-100 p-5">
                  <h2 className="font-bold text-stone-800 mb-1 text-sm">قنوات الإشعار</h2>
                  <p className="text-xs text-stone-400 mb-4">كيف تريد أن تصلك الإشعارات؟</p>
                  <div className="space-y-3">
                    {[
                      { key: 'email',    icon: 'ri-mail-line',     label: 'البريد الإلكتروني', desc: 'ahmed@example.com' },
                      { key: 'whatsapp', icon: 'ri-whatsapp-line', label: 'واتساب',            desc: '+966 5X XXX XXXX' },
                    ].map((ch) => (
                      <div key={ch.key} className="flex items-center justify-between gap-4 py-2 border-b border-stone-50 last:border-0">
                        <div className="flex items-center gap-3">
                          <div className="w-8 h-8 flex items-center justify-center rounded-xl bg-stone-50 shrink-0">
                            <i className={`${ch.icon} text-stone-500 text-sm`}></i>
                          </div>
                          <div>
                            <p className="text-sm font-semibold text-stone-700">{ch.label}</p>
                            <p className="text-xs text-stone-400">{ch.desc}</p>
                          </div>
                        </div>
                        <button
                          onClick={() => setNotifSettings((prev) => ({ ...prev, [ch.key]: !prev[ch.key as keyof typeof prev] }))}
                          className={`w-11 h-6 rounded-full transition-colors relative shrink-0 cursor-pointer ${notifSettings[ch.key as keyof typeof notifSettings] ? 'bg-emerald-500' : 'bg-stone-200'}`}
                        >
                          <div className={`absolute top-0.5 w-5 h-5 bg-white rounded-full transition-all ${notifSettings[ch.key as keyof typeof notifSettings] ? 'left-5' : 'left-0.5'}`}></div>
                        </button>
                      </div>
                    ))}
                  </div>
                </div>

                <button
                  onClick={() => showSaved('notifications')}
                  className="w-full flex items-center justify-center gap-2 py-3 rounded-xl bg-emerald-600 hover:bg-emerald-700 text-white font-bold text-sm cursor-pointer transition-colors whitespace-nowrap"
                >
                  {saved === 'notifications' ? (
                    <><i className="ri-check-line"></i> تم الحفظ!</>
                  ) : (
                    <><i className="ri-save-line"></i> حفظ التفضيلات</>
                  )}
                </button>
              </div>
            )}

            {/* ── Security Tab ── */}
            {activeTab === 'security' && (
              <div className="space-y-5">
                {/* Change Password */}
                <div className="bg-white rounded-2xl border border-stone-100 p-5">
                  <h2 className="font-bold text-stone-800 mb-4 text-sm">تغيير كلمة المرور</h2>
                  <div className="space-y-3">
                    {[
                      { label: 'كلمة المرور الحالية', value: currentPass, setter: setCurrentPass },
                      { label: 'كلمة المرور الجديدة',  value: newPass,     setter: setNewPass },
                      { label: 'تأكيد كلمة المرور',    value: confirmPass, setter: setConfirmPass },
                    ].map((field) => (
                      <div key={field.label}>
                        <label className="block text-xs font-semibold text-stone-600 mb-1.5">{field.label}</label>
                        <div className="relative">
                          <input
                            type={showPass ? 'text' : 'password'}
                            value={field.value}
                            onChange={(e) => field.setter(e.target.value)}
                            className="w-full border border-stone-200 rounded-xl px-4 py-2.5 text-sm text-stone-800 focus:outline-none focus:border-emerald-400 transition-colors pl-10"
                            dir="ltr"
                          />
                          <button
                            type="button"
                            onClick={() => setShowPass(!showPass)}
                            className="absolute left-3 top-1/2 -translate-y-1/2 text-stone-400 hover:text-stone-600 cursor-pointer"
                          >
                            <i className={`${showPass ? 'ri-eye-off-line' : 'ri-eye-line'} text-sm`}></i>
                          </button>
                        </div>
                      </div>
                    ))}

                    {/* Password strength */}
                    {newPass.length > 0 && (
                      <div>
                        <div className="flex gap-1 mt-1">
                          {[1, 2, 3, 4].map((i) => (
                            <div
                              key={i}
                              className={`h-1 flex-1 rounded-full transition-colors ${
                                newPass.length >= i * 3
                                  ? i <= 1 ? 'bg-red-400' : i <= 2 ? 'bg-amber-400' : i <= 3 ? 'bg-emerald-400' : 'bg-emerald-600'
                                  : 'bg-stone-200'
                              }`}
                            />
                          ))}
                        </div>
                        <p className="text-xs text-stone-400 mt-1">
                          {newPass.length < 4 ? 'ضعيفة جداً' : newPass.length < 7 ? 'ضعيفة' : newPass.length < 10 ? 'متوسطة' : 'قوية'}
                        </p>
                      </div>
                    )}

                    {confirmPass && newPass !== confirmPass && (
                      <p className="text-xs text-red-500 flex items-center gap-1">
                        <i className="ri-close-circle-line"></i>
                        كلمتا المرور غير متطابقتين
                      </p>
                    )}
                  </div>

                  <button
                    onClick={() => showSaved('password')}
                    disabled={!currentPass || !newPass || newPass !== confirmPass}
                    className={`mt-4 w-full flex items-center justify-center gap-2 py-3 rounded-xl font-bold text-sm cursor-pointer transition-colors whitespace-nowrap ${
                      currentPass && newPass && newPass === confirmPass
                        ? 'bg-emerald-600 hover:bg-emerald-700 text-white'
                        : 'bg-stone-100 text-stone-400 cursor-not-allowed'
                    }`}
                  >
                    {saved === 'password' ? (
                      <><i className="ri-check-line"></i> تم التغيير!</>
                    ) : (
                      <><i className="ri-lock-line"></i> تغيير كلمة المرور</>
                    )}
                  </button>
                </div>

                {/* 2FA */}
                <div className="bg-white rounded-2xl border border-stone-100 p-5">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 flex items-center justify-center bg-emerald-50 rounded-xl shrink-0">
                        <i className="ri-smartphone-line text-emerald-600 text-lg"></i>
                      </div>
                      <div>
                        <p className="text-sm font-bold text-stone-800">التحقق بخطوتين</p>
                        <p className="text-xs text-stone-400">حماية إضافية عبر رمز SMS</p>
                      </div>
                    </div>
                    <button
                      onClick={() => setTwoFactor(!twoFactor)}
                      className={`w-11 h-6 rounded-full transition-colors relative shrink-0 cursor-pointer ${twoFactor ? 'bg-emerald-500' : 'bg-stone-200'}`}
                    >
                      <div className={`absolute top-0.5 w-5 h-5 bg-white rounded-full transition-all ${twoFactor ? 'left-5' : 'left-0.5'}`}></div>
                    </button>
                  </div>
                  {twoFactor && (
                    <div className="mt-3 bg-emerald-50 rounded-xl p-3 flex items-center gap-2">
                      <i className="ri-shield-check-line text-emerald-600 text-sm shrink-0"></i>
                      <p className="text-xs text-emerald-700">التحقق بخطوتين مفعّل — حسابك محمي</p>
                    </div>
                  )}
                </div>

                {/* Danger Zone */}
                <div className="bg-white rounded-2xl border border-red-100 p-5">
                  <h2 className="font-bold text-red-600 mb-3 text-sm flex items-center gap-2">
                    <i className="ri-error-warning-line"></i>
                    منطقة الخطر
                  </h2>
                  <p className="text-xs text-stone-500 mb-4 leading-relaxed">
                    حذف الحساب إجراء لا يمكن التراجع عنه. سيتم حذف جميع بياناتك وحملاتك نهائياً.
                  </p>
                  <button className="flex items-center gap-2 px-4 py-2.5 rounded-xl border border-red-200 text-red-500 hover:bg-red-50 text-sm font-semibold cursor-pointer transition-colors whitespace-nowrap">
                    <i className="ri-delete-bin-line text-sm"></i>
                    حذف الحساب نهائياً
                  </button>
                </div>
              </div>
            )}

          </div>
        </div>
      </div>
    </div>
  );
};

export default SettingsPage;
